const DB_NAME = "aurProtectedBooks";
const DB_VERSION = 1;
const META_STORE = "books";
const PAGE_STORE = "pages";

const MAX_IN_FLIGHT = 2;
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 750;

function assertPage(page, pageCount) {
  if (!Number.isInteger(page) || page < 1 || page > pageCount) {
    throw new Error(`Invalid protected-book page: ${page}`);
  }
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: "bookKey" });
      }

      if (!db.objectStoreNames.contains(PAGE_STORE)) {
        const pages = db.createObjectStore(PAGE_STORE, {
          keyPath: ["bookKey", "page"],
        });
        pages.createIndex("bookKey", "bookKey", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      reject(request.error || new Error("Unable to open protected-book storage."));
    };
  });
}

function requestPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      reject(request.error || new Error("IndexedDB request failed."));
    };
  });
}

function transactionPromise(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => {
      reject(transaction.error || new Error("IndexedDB transaction failed."));
    };
    transaction.onabort = () => {
      reject(transaction.error || new Error("IndexedDB transaction aborted."));
    };
  });
}

async function makeBookKey(language, manifest) {
  const encoded = new TextEncoder().encode(JSON.stringify(manifest));
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  const hash = Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

  return `aur-mirror-mind:${language}:${hash}`;
}

export async function createProtectedBookStore({
  language,
  manifest,
  pageUrl,
  onProgress,
}) {
  if (!globalThis.indexedDB) {
    throw new Error(
      "IndexedDB is unavailable; protected book storage cannot start."
    );
  }

  if (typeof pageUrl !== "function") {
    throw new Error("Protected-book pageUrl must be a function.");
  }

  const pageCount = Number(manifest?.page_count);
  if (!Number.isInteger(pageCount) || pageCount < 1) {
    throw new Error(
      "Protected-book manifest contains an invalid page_count."
    );
  }

  const db = await openDatabase();

  /*
   * Request persistent storage when the browser supports it. The browser
   * may decline; this is deliberately best-effort and does not change
   * authorization or reader behavior.
   */
  try {
    if (
      globalThis.navigator?.storage &&
      typeof globalThis.navigator.storage.persist === "function"
    ) {
      await globalThis.navigator.storage.persist();
    }
  } catch {
    // Best-effort only. Continue with ordinary IndexedDB storage.
  }

  const bookKey = await makeBookKey(language, manifest);
  const readyPages = new Set();
  const objectUrls = new Map();
  const queued = new Map();
  const inFlight = new Set();
  const retryCounts = new Map();
  const waiters = new Map();
  const listeners = new Set();

  let sequence = 0;
  let pumpScheduled = false;
  let disposed = false;

  const emit = () => {
    const progress = {
      ready: readyPages.size,
      total: pageCount,
      complete: readyPages.size === pageCount,
    };

    if (typeof onProgress === "function") {
      onProgress(progress);
    }

    listeners.forEach((listener) => listener(progress));
  };

  const persistMeta = async () => {
    const transaction = db.transaction(META_STORE, "readwrite");
    transaction.objectStore(META_STORE).put({
      bookKey,
      language,
      pageCount,
      manifest,
      updatedAt: Date.now(),
    });
    await transactionPromise(transaction);
  };

  const hydrateReadyPages = async () => {
    const transaction = db.transaction(PAGE_STORE, "readonly");
    const request = transaction
      .objectStore(PAGE_STORE)
      .index("bookKey")
      .getAllKeys(IDBKeyRange.only(bookKey));
    const keys = await requestPromise(request);

    /*
     * The request result is complete before the request promise resolves.
     * Do not make the reader wait on a second transaction event here.
     */
    for (const key of keys) {
      if (Array.isArray(key)) {
        readyPages.add(Number(key[1]));
      }
    }

    emit();
  };

  const readBlob = async (page) => {
    const transaction = db.transaction(PAGE_STORE, "readonly");
    const request = transaction.objectStore(PAGE_STORE).get([
      bookKey,
      page,
    ]);
    const row = await requestPromise(request);
    return row?.blob instanceof Blob ? row.blob : null;
  };

  const writeBlob = async (page, blob) => {
    const transaction = db.transaction(PAGE_STORE, "readwrite");
    transaction.objectStore(PAGE_STORE).put({
      bookKey,
      page,
      blob,
      updatedAt: Date.now(),
    });
    await transactionPromise(transaction);
  };

  const getPageUrl = async (page) => {
    assertPage(page, pageCount);
    const existingUrl = objectUrls.get(page);
    if (existingUrl) return existingUrl;
    if (!readyPages.has(page)) return null;

    const blob = await readBlob(page);
    if (!blob) {
      readyPages.delete(page);
      emit();
      return null;
    }

    const url = URL.createObjectURL(blob);
    objectUrls.set(page, url);
    return url;
  };

  const resolvePageWaiters = async (page, error = null) => {
    const pageWaiters = waiters.get(page);
    if (!pageWaiters) return;

    waiters.delete(page);
    if (error) {
      pageWaiters.forEach(({ reject }) => reject(error));
      return;
    }

    const url = await getPageUrl(page);
    pageWaiters.forEach(({ resolve }) => resolve(url));
  };

  const chooseNext = () => {
    let selectedPage = null;
    let selectedItem = null;

    for (const [page, item] of queued) {
      if (inFlight.has(page)) continue;

      if (
        selectedItem === null ||
        item.priority < selectedItem.priority ||
        (item.priority === selectedItem.priority &&
          item.sequence < selectedItem.sequence)
      ) {
        selectedPage = page;
        selectedItem = item;
      }
    }

    return selectedPage;
  };

  const enqueue = (page, priority = 1000) => {
    if (
      disposed ||
      page < 1 ||
      page > pageCount ||
      readyPages.has(page)
    ) {
      return;
    }

    const existing = queued.get(page);
    if (existing === undefined || priority < existing.priority) {
      queued.set(page, { priority, sequence: sequence++ });
    }
  };

  const schedulePump = () => {
    if (disposed || pumpScheduled) return;

    pumpScheduled = true;
    queueMicrotask(() => {
      pumpScheduled = false;
      if (!disposed) void pump();
    });
  };

  const pump = async () => {
    while (!disposed && inFlight.size < MAX_IN_FLIGHT) {
      const page = chooseNext();
      if (page == null) break;

      queued.delete(page);
      inFlight.add(page);
      void fetchPage(page);
    }
  };

  const fetchPage = async (page) => {
    try {
      if (readyPages.has(page)) return;

      const requestUrl = pageUrl(page);
      const requestOrigin = new URL(
        requestUrl,
        window.location.href,
      ).origin;

      const response = await fetch(requestUrl, {
        method: "GET",
        credentials:
          requestOrigin === window.location.origin
            ? "include"
            : "omit",
        cache: "no-store",
        headers: {
          Accept: "image/png,image/*;q=0.8",
        },
      });

      if (response.status === 401 || response.status === 403) {
        throw Object.assign(
          new Error(
            `Protected-book authorization failed (${response.status}).`
          ),
          { code: "AUTH", status: response.status }
        );
      }

      if (!response.ok) {
        throw Object.assign(
          new Error(
            `Protected-book page ${page} failed (${response.status}).`
          ),
          { code: "HTTP", status: response.status }
        );
      }

      const blob = await response.blob();
      if (!blob.size) {
        throw new Error(
          `Protected-book page ${page} returned an empty body.`
        );
      }

      await writeBlob(page, blob);
      readyPages.add(page);
      retryCounts.delete(page);
      emit();
      await resolvePageWaiters(page);
    } catch (error) {
      const attempts = retryCounts.get(page) || 0;
      if (
        !disposed &&
        error?.code !== "AUTH" &&
        attempts < MAX_RETRIES
      ) {
        retryCounts.set(page, attempts + 1);
        globalThis.setTimeout(() => {
          if (disposed) return;
          enqueue(page, 0);
          schedulePump();
        }, RETRY_BASE_MS * 2 ** attempts);
      } else {
        await resolvePageWaiters(page, error);
      }
    } finally {
      inFlight.delete(page);
      schedulePump();
    }
  };

  const prioritize = (centerPage) => {
    assertPage(centerPage, pageCount);
    enqueue(centerPage, 0);
    enqueue(centerPage + 1, 1);
    enqueue(centerPage - 1, 2);
    enqueue(centerPage + 2, 3);
    enqueue(centerPage - 2, 4);
    schedulePump();
  };

  await persistMeta();
  await hydrateReadyPages();

  /*
   * Queue the complete book at low priority. prioritize(1) immediately
   * raises page 1 and its nearby pages.
   */
  for (let page = 1; page <= pageCount; page += 1) {
    enqueue(page, page);
  }

  prioritize(1);

  return {
    pageCount,

    isReady: (page) => {
      assertPage(page, pageCount);
      return readyPages.has(page);
    },

    getProgress: () => ({
      ready: readyPages.size,
      total: pageCount,
      complete: readyPages.size === pageCount,
    }),

    getPageUrl,
    prioritize,

    waitForPage: async (page) => {
      assertPage(page, pageCount);
      const existingUrl = await getPageUrl(page);
      if (existingUrl) return existingUrl;

      const promise = new Promise((resolve, reject) => {
        const pageWaiters = waiters.get(page) || [];
        pageWaiters.push({ resolve, reject });
        waiters.set(page, pageWaiters);
      });

      prioritize(page);
      return promise;
    },

    releasePageUrl: (page) => {
      const url = objectUrls.get(page);
      if (url) {
        URL.revokeObjectURL(url);
        objectUrls.delete(page);
      }
    },

    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    dispose: () => {
      if (disposed) return;
      disposed = true;

      waiters.forEach((pageWaiters) => {
        pageWaiters.forEach(({ reject }) => {
          reject(new Error("Protected-book store disposed."));
        });
      });

      waiters.clear();
      queued.clear();
      inFlight.clear();
      listeners.clear();

      objectUrls.forEach((url) => {
        URL.revokeObjectURL(url);
      });

      objectUrls.clear();
      db.close();
    },
  };
}